import React, { useEffect, useMemo, useState } from "react";
import { C } from "../../core/constants";
import { localizedPath } from "../../core/localePaths";
import { useI18n } from "../i18n/LocaleProvider";
import GlassCard from "../../ui/GlassCard";
import { fetchEmailVerificationStatus, resendVerification, verifyEmailOtp } from "./api";

function formatCountdown(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safe / 60).toString().padStart(2, "0");
  const seconds = (safe % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export default function EmailVerificationRequiredPage({ acc, authContext, t: tProp }) {
  const { t: tContext, locale } = useI18n();
  const t = tProp || tContext;
  const email = authContext?.user?.email || "";
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [otpActive, setOtpActive] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [resendAllowed, setResendAllowed] = useState(true);
  const [resendAvailableInSeconds, setResendAvailableInSeconds] = useState(0);
  const [referenceCode, setReferenceCode] = useState("");
  const [otpDeadlineAt, setOtpDeadlineAt] = useState(null);
  const [resendDeadlineAt, setResendDeadlineAt] = useState(null);

  const applyVerificationState = (response) => {
    const now = Date.now();
    const nextOtpActive = Boolean(response?.otp_active);
    const nextOtpSeconds = Math.max(0, Number(response?.otp_expires_in_seconds) || 0);
    const nextResendAllowed = Boolean(response?.resend_allowed);
    const nextResendSeconds = Math.max(0, Number(response?.resend_available_in_seconds) || 0);

    setOtpActive(nextOtpActive);
    setRemainingSeconds(nextOtpSeconds);
    setResendAllowed(nextResendAllowed);
    setResendAvailableInSeconds(nextResendSeconds);
    setOtpDeadlineAt(nextOtpActive ? now + (nextOtpSeconds * 1000) : null);
    setResendDeadlineAt(!nextResendAllowed && nextResendSeconds > 0 ? now + (nextResendSeconds * 1000) : null);
    if (response?.reference_code) setReferenceCode(response.reference_code);
  };

  const syncStatus = async () => {
    const response = await fetchEmailVerificationStatus();
    applyVerificationState(response);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await syncStatus();
      } catch (e) {
        if (!cancelled) setError(e.message || t("auth_error_unable_to_continue"));
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();

      if (otpDeadlineAt) {
        const otpLeft = Math.max(0, Math.ceil((otpDeadlineAt - now) / 1000));
        setRemainingSeconds(otpLeft);
      }

      if (resendDeadlineAt) {
        const resendLeft = Math.max(0, Math.ceil((resendDeadlineAt - now) / 1000));
        setResendAvailableInSeconds(resendLeft);
      }
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [otpDeadlineAt, resendDeadlineAt]);

  useEffect(() => {
    if (!otpActive || remainingSeconds > 0) return;
    setOtpActive(false);
    setOtpDeadlineAt(null);
  }, [otpActive, remainingSeconds]);

  useEffect(() => {
    if (resendAllowed || resendAvailableInSeconds > 0) return;
    setResendAllowed(true);
    setResendDeadlineAt(null);
  }, [resendAllowed, resendAvailableInSeconds]);

  const effectiveResendAllowed = resendAllowed || resendAvailableInSeconds <= 0;
  const countdown = useMemo(() => formatCountdown(otpActive ? remainingSeconds : resendAvailableInSeconds), [otpActive, remainingSeconds, resendAvailableInSeconds]);
  const canSendOtp = !otpActive && effectiveResendAllowed && !loadingResend;

  const submitOtp = async (event) => {
    event.preventDefault();
    if (loadingVerify || !otp.trim()) return;

    setError("");
    setNotice("");
    setLoadingVerify(true);
    try {
      await verifyEmailOtp({ email, otp: otp.trim() });
      window.location.assign(localizedPath("dashboard", locale));
    } catch (e) {
      setError(e.message || t("auth_error_unable_to_continue"));
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleResend = async () => {
    if (!canSendOtp) return;

    setError("");
    setNotice("");
    setReferenceCode("");
    setLoadingResend(true);
    try {
      const response = await resendVerification({ email });
      if (response?.message) setNotice(response.message);
      if (response?.code === "sent") {
        applyVerificationState(response);
      } else {
        await syncStatus();
      }
    } catch (e) {
      setError(e.message || t("auth_error_unable_to_continue"));
    } finally {
      setLoadingResend(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        <GlassCard style={{ padding: 36 }} glow accent={acc.primary}>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 42, color: C.textPrimary, margin: 0 }}>
            {t("auth_verify_required_title")}
          </h1>
          <p style={{ color: C.textSecondary, marginTop: 12, marginBottom: 0 }}>
            {t("auth_verify_required_description")}
          </p>
          <p style={{ color: acc.primary, marginTop: 8, marginBottom: 24, fontWeight: 600 }}>
            {email}
          </p>

          {loadingStatus ? (
            <p style={{ color: C.textSecondary, margin: 0 }}>{t("auth_loading_wait")}</p>
          ) : otpActive ? (
            <>
              <form onSubmit={submitOtp}>
                <label style={{ display: "block", marginBottom: 8, color: acc.primary, fontSize: 12, letterSpacing: "0.08em" }}>
                  {t("auth_label_otp_upper")}
                </label>
                <input
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  placeholder={t("auth_placeholder_otp")}
                  autoComplete="one-time-code"
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    padding: "13px 16px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.04)",
                    color: C.textPrimary,
                    fontSize: 16,
                    letterSpacing: "0.1em",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  type="submit"
                  disabled={loadingVerify}
                  style={{
                    width: "100%",
                    marginTop: 16,
                    border: "none",
                    borderRadius: 12,
                    padding: "13px 16px",
                    cursor: "pointer",
                    background: `linear-gradient(135deg,${acc.primary},${acc.tertiary})`,
                    color: "#fff",
                    fontWeight: 600,
                    opacity: loadingVerify ? 0.7 : 1,
                  }}
                >
                  {loadingVerify ? t("auth_loading_wait") : t("auth_verify_submit")}
                </button>
              </form>

              <div style={{ marginTop: 20, color: C.textSecondary, fontSize: 13 }}>
                {t("auth_verification_countdown")}: <strong style={{ color: acc.primary }}>{countdown}</strong>
              </div>
              {referenceCode ? (
                <div style={{ marginTop: 10, color: C.textSecondary, fontSize: 13 }}>
                  {t("auth_reference_code_label")}: <strong style={{ color: acc.primary }}>{referenceCode}</strong>
                </div>
              ) : null}
            </>
          ) : (
            <div
              style={{
                marginTop: 10,
                padding: "16px 16px 14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <p style={{ color: C.textSecondary, marginTop: 0, marginBottom: 12, lineHeight: 1.55 }}>
                {t("auth_no_active_verification")}
              </p>
              {effectiveResendAllowed ? null : (
                <p style={{ color: C.textMuted, marginTop: 0, marginBottom: 12, fontSize: 12 }}>
                  {t("auth_verification_countdown")}: <strong style={{ color: acc.primary }}>{countdown}</strong>
                </p>
              )}
              {referenceCode ? (
                <p style={{ color: C.textSecondary, marginTop: 0, marginBottom: 12, fontSize: 12 }}>
                  {t("auth_reference_code_label")}: <strong style={{ color: acc.primary }}>{referenceCode}</strong>
                </p>
              ) : null}
              <button
                type="button"
                onClick={handleResend}
                disabled={!canSendOtp}
                style={{
                  width: "100%",
                  borderRadius: 12,
                  border: "none",
                  background: canSendOtp
                    ? `linear-gradient(135deg,${acc.primary},${acc.tertiary})`
                    : "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
                  boxShadow: canSendOtp ? `0 0 24px ${acc.primary}55` : "none",
                  color: "#fff",
                  padding: "12px 14px",
                  cursor: canSendOtp ? "pointer" : "not-allowed",
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: "0.02em",
                  fontFamily: "'DM Sans',sans-serif",
                  textAlign: "center",
                  opacity: loadingResend ? 0.7 : 1,
                  transition: "all 0.2s ease",
                }}
              >
                {loadingResend ? t("auth_loading_wait") : t("auth_send_verification_otp")}
              </button>
            </div>
          )}

          {error ? <p style={{ color: "#ff9ca8", marginTop: 14, marginBottom: 0 }}>{error}</p> : null}
          {notice ? <p style={{ color: acc.primary, marginTop: 14, marginBottom: 0 }}>{notice}</p> : null}
        </GlassCard>
      </div>
    </div>
  );
}
